import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { ScrollArea } from "../ui/scroll-area";
import { Card, CardContent } from "../ui/card";
import {
  getEventTypes,
  getWeekEvents,
  getUpcomingEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  updateEventStatus,
} from "../../lib/api";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  FileText,
  Bell,
  Repeat,
  Trash2,
  Plus,
  Check,
  X,
  AlertCircle,
  Bus,
  Building,
  GraduationCap,
  UserCircle,
  Waves,
  BookOpen,
  ClipboardCheck,
  Presentation,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const ICON_MAP = {
  Users: Users,
  Building: Building,
  GraduationCap: GraduationCap,
  Bus: Bus,
  UserCircle: UserCircle,
  Waves: Waves,
  BookOpen: BookOpen,
  ClipboardCheck: ClipboardCheck,
  AlertCircle: AlertCircle,
  Presentation: Presentation,
  Calendar: Calendar,
};

const STATUS_LABELS = {
  planifie: { label: "Planifié", color: "bg-blue-100 text-blue-700" },
  confirme: { label: "Confirmé", color: "bg-green-100 text-green-700" },
  annule: { label: "Annulé", color: "bg-red-100 text-red-700" },
  termine: { label: "Terminé", color: "bg-slate-100 text-slate-700" },
};

function EventCard({ event, eventTypes, onEdit, onStatusChange, onDelete }) {
  const eventType = eventTypes.find(t => t.id === event.event_type) || eventTypes[eventTypes.length - 1];
  const Icon = ICON_MAP[eventType?.icon] || Calendar;
  const statusInfo = STATUS_LABELS[event.status] || STATUS_LABELS.planifie;

  return (
    <Card 
      className="cursor-pointer hover:shadow-md transition-all border-l-4"
      style={{ borderLeftColor: eventType?.color || "#64748B" }}
      onClick={() => onEdit(event)}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${eventType?.color}20` }}
            >
              <Icon className="w-4 h-4" style={{ color: eventType?.color }} />
            </div>
            <div className="min-w-0">
              <div className="font-medium text-sm truncate">{event.title}</div>
              <div className="text-xs text-slate-500">
                {event.start_time && event.end_time 
                  ? `${event.start_time} - ${event.end_time}`
                  : event.all_day 
                    ? "Toute la journée" 
                    : ""
                }
              </div>
            </div>
          </div>
          <Badge className={`text-[10px] ${statusInfo.color}`}>
            {statusInfo.label}
          </Badge>
        </div>
        
        {(event.location || event.participants) && (
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
            {event.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {event.location}
              </span>
            )}
            {event.participants && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {event.participants}
              </span>
            )}
          </div>
        )}
        
        {event.niveaux && event.niveaux.length > 0 && (
          <div className="mt-2 flex gap-1 flex-wrap">
            {event.niveaux.map(niveau => (
              <Badge key={niveau} variant="secondary" className="text-[9px] px-1.5 py-0">
                {niveau.toUpperCase()}
              </Badge>
            ))}
          </div>
        )}
        
        <div className="mt-2 flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => onStatusChange(event.id, "termine")}
          >
            <Check className="w-3 h-3 mr-1 text-green-600" />
            Fait
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => onStatusChange(event.id, "annule")}
          >
            <X className="w-3 h-3 mr-1 text-red-600" />
            Annuler
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs ml-auto"
            onClick={() => onDelete(event.id)}
          >
            <Trash2 className="w-3 h-3 text-slate-400" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EventEditorForm({ event, eventTypes, onSave, onCancel, saving }) {
  const [formData, setFormData] = useState({
    title: "",
    event_type: "autre",
    description: "",
    date: new Date().toISOString().split("T")[0],
    start_time: "",
    end_time: "",
    all_day: false,
    location: "",
    participants: "",
    niveaux: [],
    reminder: false,
    reminder_days_before: 1,
    notes: "",
    recurrence: "",
    recurrence_end_date: "",
  });

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || "",
        event_type: event.event_type || "autre",
        description: event.description || "",
        date: event.date || new Date().toISOString().split("T")[0],
        start_time: event.start_time || "",
        end_time: event.end_time || "",
        all_day: event.all_day || false,
        location: event.location || "",
        participants: event.participants || "",
        niveaux: event.niveaux || [],
        reminder: event.reminder || false,
        reminder_days_before: event.reminder_days_before || 1,
        notes: event.notes || "",
        recurrence: event.recurrence || "",
        recurrence_end_date: event.recurrence_end_date || "",
      });
    }
  }, [event]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleNiveau = (niveau) => {
    setFormData(prev => ({
      ...prev,
      niveaux: prev.niveaux.includes(niveau)
        ? prev.niveaux.filter(n => n !== niveau)
        : [...prev.niveaux, niveau]
    }));
  };

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      toast.error("Le titre est requis");
      return;
    }
    onSave(formData);
  };

  const selectedType = eventTypes.find(t => t.id === formData.event_type);

  return (
    <div className="space-y-4">
      {/* Type */}
      <div>
        <Label>Type d'événement</Label>
        <Select value={formData.event_type} onValueChange={v => handleChange("event_type", v)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {eventTypes.map(type => {
              const Icon = ICON_MAP[type.icon] || Calendar;
              return (
                <SelectItem key={type.id} value={type.id}>
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" style={{ color: type.color }} />
                    {type.label}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Title */}
      <div>
        <Label>Titre *</Label>
        <Input
          value={formData.title}
          onChange={e => handleChange("title", e.target.value)}
          placeholder="Ex: Réunion parents CP"
        />
      </div>

      {/* Date & Time */}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label>Date *</Label>
          <Input
            type="date"
            value={formData.date}
            onChange={e => handleChange("date", e.target.value)}
          />
        </div>
        <div>
          <Label>Début</Label>
          <Input
            type="time"
            value={formData.start_time}
            onChange={e => handleChange("start_time", e.target.value)}
            disabled={formData.all_day}
          />
        </div>
        <div>
          <Label>Fin</Label>
          <Input
            type="time"
            value={formData.end_time}
            onChange={e => handleChange("end_time", e.target.value)}
            disabled={formData.all_day}
          />
        </div>
      </div>

      {/* All day toggle */}
      <div className="flex items-center gap-2">
        <Switch
          checked={formData.all_day}
          onCheckedChange={v => handleChange("all_day", v)}
        />
        <Label className="font-normal">Toute la journée</Label>
      </div>

      {/* Location & Participants */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Lieu</Label>
          <Input
            value={formData.location}
            onChange={e => handleChange("location", e.target.value)}
            placeholder="Ex: Salle polyvalente"
          />
        </div>
        <div>
          <Label>Participants</Label>
          <Input
            value={formData.participants}
            onChange={e => handleChange("participants", e.target.value)}
            placeholder="Ex: Parents, directeur"
          />
        </div>
      </div>

      {/* Niveaux */}
      <div>
        <Label>Classes concernées</Label>
        <div className="flex flex-wrap gap-1 mt-1">
          {["ps", "ms", "gs", "cp", "ce1", "ce2", "cm1", "cm2"].map(niveau => (
            <Badge
              key={niveau}
              variant={formData.niveaux.includes(niveau) ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => toggleNiveau(niveau)}
            >
              {niveau.toUpperCase()}
            </Badge>
          ))}
        </div>
      </div>

      {/* Description */}
      <div>
        <Label>Description</Label>
        <Textarea
          value={formData.description}
          onChange={e => handleChange("description", e.target.value)}
          placeholder="Détails de l'événement..."
          rows={2}
        />
      </div>

      {/* Reminder */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Switch
            checked={formData.reminder}
            onCheckedChange={v => handleChange("reminder", v)}
          />
          <Label className="font-normal flex items-center gap-1">
            <Bell className="w-4 h-4" />
            Rappel
          </Label>
        </div>
        {formData.reminder && (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="1"
              max="30"
              value={formData.reminder_days_before}
              onChange={e => handleChange("reminder_days_before", parseInt(e.target.value) || 1)}
              className="w-16 h-8"
            />
            <span className="text-sm text-slate-500">jour(s) avant</span>
          </div>
        )}
      </div>

      {/* Recurrence */}
      {!event && (
        <div className="space-y-2">
          <Label className="flex items-center gap-1">
            <Repeat className="w-4 h-4" />
            Récurrence
          </Label>
          <div className="grid grid-cols-2 gap-2">
            <Select value={formData.recurrence || "none"} onValueChange={v => handleChange("recurrence", v === "none" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Aucune" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune</SelectItem>
                <SelectItem value="weekly">Hebdomadaire</SelectItem>
                <SelectItem value="biweekly">Toutes les 2 semaines</SelectItem>
                <SelectItem value="monthly">Mensuel</SelectItem>
              </SelectContent>
            </Select>
            {formData.recurrence && (
              <Input
                type="date"
                value={formData.recurrence_end_date}
                onChange={e => handleChange("recurrence_end_date", e.target.value)}
                placeholder="Date de fin"
              />
            )}
          </div>
        </div>
      )}

      {/* Notes */}
      <div>
        <Label>Notes</Label>
        <Textarea
          value={formData.notes}
          onChange={e => handleChange("notes", e.target.value)}
          placeholder="Notes personnelles..."
          rows={2}
        />
      </div>

      {/* Actions */}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : event ? "Enregistrer" : "Créer"}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function JournalEventsDialog({ open, onOpenChange, initialDate }) {
  const [eventTypes, setEventTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(initialDate || new Date());
  const [editingEvent, setEditingEvent] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch event types
  useEffect(() => {
    getEventTypes().then(res => setEventTypes(res.data)).catch(console.error);
  }, []);

  // Fetch events for current week
  useEffect(() => {
    if (!open) return;
    fetchEvents();
  }, [open, currentDate]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const dateStr = currentDate instanceof Date 
        ? currentDate.toISOString().split("T")[0]
        : currentDate;
      const res = await getWeekEvents(dateStr);
      setEvents(res.data.events || []);
    } catch (err) {
      console.error("Error fetching events:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const handleCreateEvent = () => {
    setEditingEvent(null);
    setIsEditorOpen(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setIsEditorOpen(true);
  };

  const handleSaveEvent = async (formData) => {
    setSaving(true);
    try {
      if (editingEvent) {
        await updateEvent(editingEvent.id, formData);
        toast.success("Événement mis à jour");
      } else {
        const res = await createEvent(formData);
        toast.success(res.data.message || "Événement créé");
      }
      setIsEditorOpen(false);
      fetchEvents();
    } catch (err) {
      console.error("Error saving event:", err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (eventId, status) => {
    try {
      await updateEventStatus(eventId, status);
      toast.success(`Statut mis à jour: ${STATUS_LABELS[status]?.label}`);
      fetchEvents();
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error("Erreur");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    try {
      await deleteEvent(eventId);
      toast.success("Événement supprimé");
      fetchEvents();
    } catch (err) {
      console.error("Error deleting event:", err);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Group events by date
  const eventsByDate = events.reduce((acc, event) => {
    if (!acc[event.date]) acc[event.date] = [];
    acc[event.date].push(event);
    return acc;
  }, {});

  // Format week range
  const formatWeekRange = () => {
    const d = new Date(currentDate);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const options = { day: "numeric", month: "short" };
    return `${monday.toLocaleDateString("fr-FR", options)} - ${sunday.toLocaleDateString("fr-FR", options)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Événements
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrevWeek}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-normal min-w-[140px] text-center">
                {formatWeekRange()}
              </span>
              <Button variant="outline" size="sm" onClick={handleNextWeek}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {isEditorOpen ? (
          <ScrollArea className="max-h-[60vh]">
            <EventEditorForm
              event={editingEvent}
              eventTypes={eventTypes}
              onSave={handleSaveEvent}
              onCancel={() => setIsEditorOpen(false)}
              saving={saving}
            />
          </ScrollArea>
        ) : (
          <>
            <div className="flex justify-end">
              <Button onClick={handleCreateEvent} size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Nouvel événement
              </Button>
            </div>

            <ScrollArea className="max-h-[50vh]">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun événement cette semaine</p>
                  <Button variant="link" onClick={handleCreateEvent} className="mt-2">
                    Créer un événement
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(eventsByDate).sort().map(([date, dateEvents]) => (
                    <div key={date}>
                      <div className="text-sm font-medium text-slate-500 mb-2 sticky top-0 bg-white py-1">
                        {new Date(date).toLocaleDateString("fr-FR", { 
                          weekday: "long", 
                          day: "numeric", 
                          month: "long" 
                        })}
                      </div>
                      <div className="space-y-2">
                        {dateEvents.map(event => (
                          <EventCard
                            key={event.id}
                            event={event}
                            eventTypes={eventTypes}
                            onEdit={handleEditEvent}
                            onStatusChange={handleStatusChange}
                            onDelete={handleDeleteEvent}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
